import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, CheckCircle2, Clock, FileText, Lock, ShieldCheck } from "lucide-react";

const LINES = ["Every visit.", "Every exchange.", "One calm record."];

const BADGES = [
  { icon: ShieldCheck, label: "Case-Ready Exports" },
  { icon: Clock, label: "Punctuality Tracking" },
  { icon: Lock, label: "End-to-End Encrypted" },
];

const MaskedLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-2 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const TrackerCard = () => (
  <div className="w-72 rounded-2xl bg-paper/95 backdrop-blur border border-ink/10 shadow-[0_24px_60px_-20px_rgba(26,25,24,0.35)] p-5">
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-light">Today — Supervised Visit</span>
      <span className="flex h-2 w-2 rounded-full bg-sage animate-pulse" />
    </div>
    <div className="mt-4 flex items-start gap-3">
      <CheckCircle2 className="h-5 w-5 text-sage mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-semibold leading-snug">Visit approved &amp; on time</p>
        <p className="text-xs text-ink-muted mt-0.5">Family Center, Room 2 · Arrived 4:58 PM</p>
      </div>
    </div>
    <div className="mt-4 grid grid-cols-7 gap-1">
      {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
        <div
          key={i}
          className={`aspect-square rounded-md flex items-center justify-center text-[10px] font-mono ${
            i === 2 || i === 5 ? "bg-clay text-paper" : i === 4 ? "bg-sage text-paper" : "bg-paper-3 text-ink-muted"
          }`}
        >
          {d}
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-center gap-2 rounded-lg bg-sage/10 border border-sage/20 px-3 py-2">
      <FileText className="h-3.5 w-3.5 text-sage" />
      <span className="text-[11px] font-medium text-sage">Case note export ready · PDF</span>
    </div>
  </div>
);

export default function Hero() {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 130, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 130, damping: 16 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={sectionRef} className="relative pt-36 pb-24 sm:pt-44 sm:pb-32" data-testid="hero-section">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(90%_60%_at_70%_10%,rgba(212,163,89,0.14),transparent_60%)]" />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-clay font-semibold"
            data-testid="hero-eyebrow"
          >
            Dual Care — Visitation, Perfected
          </motion.p>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]" data-testid="hero-headline">
            {LINES.map((l, i) => (
              <MaskedLine key={l} text={l} i={i} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-ink-muted leading-relaxed"
            data-testid="hero-subheadline"
          >
            The visitation tracker that keeps parents and social workers on the same page.
            Visit approvals, punctuality tracking, engagement and case notes — all in one
            calm, secure place.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <motion.a
              href="https://dualcare4u.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-start-here-btn"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 rounded-full bg-clay text-paper font-medium px-7 py-3.5 hover:bg-clay-dark transition-colors duration-300 shadow-[0_12px_30px_-10px_rgba(200,109,81,0.5)]"
            >
              Start Here <ArrowRight className="h-4 w-4" />
            </motion.a>
            <motion.a
              href="#manifesto"
              data-testid="hero-demo-btn"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-medium hover:border-ink/50 hover:bg-paper-2 transition-colors duration-300"
            >
              See how it works
            </motion.a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-6 text-sm text-ink-muted"
          >
            Parents &amp; social workers — already onboard?{" "}
            <a
              href="https://dualcare4u.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-app-link"
              className="font-medium text-clay underline decoration-clay/40 underline-offset-4 hover:text-clay-dark transition-colors duration-300"
            >
              Access your workspace at dualcare4u.com
            </a>
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
          >
            {BADGES.map((b) => (
              <span key={b.label} className="inline-flex items-center gap-2 text-xs font-medium text-ink-muted" data-testid={`hero-badge-${b.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <b.icon className="h-4 w-4 text-sage" /> {b.label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div style={{ y: imgY }} className="relative" data-testid="hero-image-frame">
            <div className="spotlight-frame overflow-hidden rounded-[2rem] border border-ink/10 shadow-[0_40px_80px_-30px_rgba(26,25,24,0.4)]">
              <img
                src="https://images.unsplash.com/photo-1624272864537-8ecc72b67958?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhdGhlciUyMGRhdWdodGVyJTIwd2FybSUyMHN1bmxpZ2h0JTIwZmFtaWx5fGVufDB8fHx8MTc4ODE5MDE4NHww&ixlib=rb-4.1.0&q=85"
                alt="Father embracing his daughter in warm sunlight"
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
          <motion.div
            style={{ rotateX: rx, rotateY: ry, y: cardY, transformPerspective: 900 }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -left-6 sm:-left-16 bottom-10"
            data-testid="hero-tracker-card"
          >
            <TrackerCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
